import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useI18n } from '@/hooks/useI18n';
import { useChartTooltipTrigger } from '@/hooks/useChartTooltipTrigger';
import { formatInt, formatNumber } from '@/lib/format';
import type { ConversionsResponse } from '@/types/api';
import { DestinationClickChart } from './DestinationClickChart';

export function ConversionCharts({ data }: { data: ConversionsResponse }) {
  const { t, locale } = useI18n();
  const trigger = useChartTooltipTrigger();
  const dateLabel = (value: string) =>
    new Date(`${value.slice(0, 10)}T00:00:00Z`).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
  const tick = { fill: 'hsl(var(--text-3))', fontSize: 10 };
  const points = data.accounts.filter(
    (account) =>
      account.views !== null && account.clicks !== null && account.clicksPerThousandViews !== null,
  );
  const coefficient =
    data.correlation.sampleSize >= 3 && data.correlation.coefficient !== null
      ? data.correlation.coefficient.toLocaleString(locale, { maximumFractionDigits: 2 })
      : '—';
  const hasSeries = data.series.some((point) => point.clicks !== null || point.views !== null);

  return (
    <div className="space-y-4">
      <DestinationClickChart data={data} />
      <div className="rounded-[14px] border border-line-1 bg-bg-1 p-4 sm:p-5">
        <h3 className="text-[14px] font-semibold text-text-1">
          {t(
            'Comparer les clics et les vues · comptes comparables',
            'Compare clicks and views · comparable accounts',
          )}
        </h3>
        <p className="mt-1 text-[11px] leading-5 text-text-3">
          {t(
            `Comptes comparables : ${formatInt(data.summary.comparableAccounts)} · ${formatInt(data.summary.comparableClicks)} clics / ${formatInt(data.summary.comparableViews)} vues.`,
            `Comparable accounts: ${formatInt(data.summary.comparableAccounts)} · ${formatInt(data.summary.comparableClicks)} clicks / ${formatInt(data.summary.comparableViews)} views.`,
          )}{' '}
          {t(
            'Périmètre limité aux liens individuels stables et aux vues mesurées. Journées UTC terminées.',
            'Limited to stable individual links and measured views. Completed UTC days.',
          )}
        </p>
        {hasSeries ? (
          <div
            className="mt-4 h-[260px] min-w-0"
            role="img"
            aria-label={t(
              'Clics à gauche, vues à droite, par jour UTC',
              'Clicks on the left, views on the right, per UTC day',
            )}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data.series}
                margin={{ top: 8, right: 0, bottom: 0, left: 0 }}
                accessibilityLayer
              >
                <CartesianGrid vertical={false} stroke="hsl(var(--line-1))" />
                <XAxis
                  dataKey="date"
                  tickFormatter={dateLabel}
                  tick={tick}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={35}
                />
                <YAxis
                  yAxisId="clicks"
                  tickFormatter={formatNumber}
                  tick={tick}
                  tickLine={false}
                  axisLine={false}
                  width={46}
                />
                <YAxis
                  yAxisId="views"
                  orientation="right"
                  tickFormatter={formatNumber}
                  tick={tick}
                  tickLine={false}
                  axisLine={false}
                  width={46}
                />
                <Tooltip
                  trigger={trigger}
                  labelFormatter={(value) => `${dateLabel(String(value))} · UTC`}
                  formatter={(value: number) => formatInt(value)}
                  filterNull={false}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line
                  yAxisId="clicks"
                  dataKey="clicks"
                  name={t('Clics destination · axe gauche', 'Destination clicks · left axis')}
                  stroke="hsl(var(--emerald))"
                  strokeWidth={2.5}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
                <Line
                  yAxisId="views"
                  dataKey="views"
                  name={t('Vues · axe droit', 'Views · right axis')}
                  stroke="hsl(var(--violet))"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="py-10 text-center text-[13px] text-text-3">
            {t('Pas encore de journées comparables.', 'No comparable days yet.')}
          </p>
        )}
        <p className="mt-2 text-[11px] leading-5 text-text-3">
          {t(
            'Les axes ont des échelles différentes. Les interruptions sont des données manquantes, jamais remplacées par zéro.',
            'The axes use different scales. Gaps represent missing data and are never replaced with zero.',
          )}
        </p>
      </div>
      <div className="grid min-w-0 gap-4 lg:grid-cols-2">
        <div className="min-w-0 rounded-[14px] border border-line-1 bg-bg-1 p-4 sm:p-5">
          <h3 className="text-[14px] font-semibold text-text-1">
            {t('Clics pour 1 000 vues au fil du temps', 'Clicks per 1,000 views over time')}
          </h3>
          <div
            className="mt-4 h-[210px]"
            role="img"
            aria-label={t(
              'Ratio quotidien de clics pour mille vues',
              'Daily ratio of clicks per thousand views',
            )}
          >
            {data.series.some((point) => point.clicksPerThousandViews !== null) ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data.series}
                  margin={{ top: 8, right: 12, left: -8, bottom: 0 }}
                  accessibilityLayer
                >
                  <CartesianGrid vertical={false} stroke="hsl(var(--line-1))" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={dateLabel}
                    tick={tick}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={35}
                  />
                  <YAxis tick={tick} tickLine={false} axisLine={false} width={50} />
                  <Tooltip
                    trigger={trigger}
                    labelFormatter={(value) => `${dateLabel(String(value))} · UTC`}
                    formatter={(value: number) =>
                      value == null
                        ? '—'
                        : value.toLocaleString(locale, { maximumFractionDigits: 2 })
                    }
                    filterNull={false}
                  />
                  <Line
                    dataKey="clicksPerThousandViews"
                    name={t('Clics / 1 000 vues', 'Clicks / 1,000 views')}
                    stroke="hsl(var(--emerald))"
                    strokeWidth={2.5}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="pt-16 text-center text-[12px] text-text-3">
                {t(
                  'Ratio indisponible sans vues et clics comparables.',
                  'Ratio unavailable without comparable views and clicks.',
                )}
              </p>
            )}
          </div>
        </div>
        <div className="min-w-0 rounded-[14px] border border-line-1 bg-bg-1 p-4 sm:p-5">
          <h3 className="text-[14px] font-semibold text-text-1">
            {t(
              'Quels comptes transforment les vues en clics ?',
              'Which accounts turn views into clicks?',
            )}
          </h3>
          <p className="mt-2 text-[12px] text-text-2">
            {t(
              `Corrélation de Pearson : ${coefficient} · ${formatInt(data.correlation.sampleSize)} comptes comparables`,
              `Pearson correlation: ${coefficient} · ${formatInt(data.correlation.sampleSize)} comparable accounts`,
            )}
          </p>
          {points.length > 0 && (
            <div
              className="mt-3 h-[155px]"
              role="img"
              aria-label={t(
                'Comparaison par compte : vues horizontales et clics verticaux',
                'Account comparison: horizontal views and vertical clicks',
              )}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 8, right: 12, bottom: 0, left: -8 }}>
                  <CartesianGrid stroke="hsl(var(--line-1))" />
                  <XAxis
                    type="number"
                    dataKey="views"
                    name={t('Vues', 'Views')}
                    tickFormatter={formatNumber}
                    tick={tick}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    type="number"
                    dataKey="clicks"
                    name={t('Clics', 'Clicks')}
                    tickFormatter={formatNumber}
                    tick={tick}
                    tickLine={false}
                    axisLine={false}
                    width={50}
                  />
                  <Tooltip
                    trigger={trigger}
                    content={({ active, payload }) => {
                      const account = payload?.[0]?.payload as (typeof points)[number] | undefined;
                      return active && account ? (
                        <div className="rounded-lg border border-line-1 bg-bg-1 p-3 text-[12px] shadow-card">
                          <strong>@{account.username}</strong>
                          <p>
                            {formatInt(account.views)} {t('vues', 'views')} ·{' '}
                            {formatInt(account.clicks)} {t('clics', 'clicks')}
                          </p>
                        </div>
                      ) : null;
                    }}
                  />
                  <Scatter data={points} fill="hsl(var(--emerald))" isAnimationActive={false} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="mt-3 text-[11px] leading-5 text-text-3">
            {t(
              'Vues en horizontal, clics en vertical. La corrélation décrit une association, pas une causalité. Au moins 3 comptes et des valeurs variables sont nécessaires ; un petit échantillon reste fragile.',
              'Views horizontally, clicks vertically. Correlation describes an association, not causation. At least 3 accounts and varying values are required; small samples remain uncertain.',
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
